import { syntaxTree } from "@codemirror/language";
import { linter, lintGutter, type Diagnostic } from "@codemirror/lint";
import type { EditorView } from "@codemirror/view";
import type { SyntaxNode, Tree } from "@lezer/common";
import { PYTHON_STDLIB_MODULES } from "../../lib/board/codePackages";
import type { CodeEditorLanguage } from "./codeEditorView";

const SYNTAX_LABEL: Record<CodeEditorLanguage, string> = {
  python: "Синтаксическая ошибка Python",
  javascript: "Синтаксическая ошибка JavaScript",
  html: "Ошибка разметки HTML",
  css: "Ошибка CSS",
  sql: "Синтаксическая ошибка SQL",
};

/** Часто используемые встроенные имена Python (урок / Pyodide). */
const PYTHON_BUILTINS = new Set([
  "False",
  "None",
  "True",
  "__import__",
  "abs",
  "aiter",
  "all",
  "anext",
  "any",
  "ascii",
  "bin",
  "bool",
  "breakpoint",
  "bytearray",
  "bytes",
  "callable",
  "chr",
  "classmethod",
  "compile",
  "complex",
  "delattr",
  "dict",
  "dir",
  "divmod",
  "enumerate",
  "eval",
  "exec",
  "filter",
  "float",
  "format",
  "frozenset",
  "getattr",
  "globals",
  "hasattr",
  "hash",
  "help",
  "hex",
  "id",
  "input",
  "int",
  "isinstance",
  "issubclass",
  "iter",
  "len",
  "list",
  "locals",
  "map",
  "max",
  "memoryview",
  "min",
  "next",
  "object",
  "oct",
  "open",
  "ord",
  "pow",
  "print",
  "property",
  "range",
  "repr",
  "reversed",
  "round",
  "set",
  "setattr",
  "slice",
  "sorted",
  "staticmethod",
  "str",
  "sum",
  "super",
  "tuple",
  "type",
  "vars",
  "zip",
]);

const JS_BUILTINS = new Set([
  "undefined",
  "NaN",
  "Infinity",
  "console",
  "Math",
  "JSON",
  "Array",
  "Object",
  "String",
  "Number",
  "Boolean",
  "Date",
  "RegExp",
  "Error",
  "Map",
  "Set",
  "WeakMap",
  "WeakSet",
  "Promise",
  "Symbol",
  "BigInt",
  "Proxy",
  "Reflect",
  "Intl",
  "parseInt",
  "parseFloat",
  "isNaN",
  "isFinite",
  "encodeURI",
  "decodeURI",
  "encodeURIComponent",
  "decodeURIComponent",
  "setTimeout",
  "clearTimeout",
  "setInterval",
  "clearInterval",
  "requestAnimationFrame",
  "cancelAnimationFrame",
  "fetch",
  "URL",
  "URLSearchParams",
  "atob",
  "btoa",
  "structuredClone",
  "document",
  "window",
  "globalThis",
  "self",
  "navigator",
  "location",
  "history",
  "localStorage",
  "sessionStorage",
  "alert",
  "confirm",
  "prompt",
  "performance",
  "crypto",
]);

function isStrictlyInside(
  inner: { from: number; to: number },
  outer: { from: number; to: number }
): boolean {
  return (
    inner.from >= outer.from &&
    inner.to <= outer.to &&
    (inner.from > outer.from || inner.to < outer.to)
  );
}

function textAt(doc: EditorView["state"]["doc"], node: { from: number; to: number }): string {
  return doc.sliceString(node.from, node.to);
}

function collectSyntaxDiagnostics(view: EditorView, language: CodeEditorLanguage): Diagnostic[] {
  const errors: { from: number; to: number }[] = [];
  syntaxTree(view.state).iterate({
    enter(node) {
      if (node.type.isError) errors.push({ from: node.from, to: node.to });
    },
  });
  if (!errors.length) return [];

  const deepest = errors.filter(
    (a) => !errors.some((b) => b !== a && isStrictlyInside(b, a))
  );
  const docLen = view.state.doc.length;
  const label = SYNTAX_LABEL[language];
  const seen = new Set<string>();
  const out: Diagnostic[] = [];

  for (const err of deepest) {
    let from = err.from;
    let to = err.to;
    if (to <= from) to = Math.min(docLen, from + 1);
    const key = `${from}:${to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ from, to, severity: "error", message: label, source: "syntax" });
  }
  return out;
}

/** VariableName слева от AssignOp внутри узла — определения. */
function collectAssignedNames(
  node: SyntaxNode,
  doc: EditorView["state"]["doc"],
  into: Set<string>
) {
  let sawAssign = false;
  for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
    if (ch.name === "AssignOp" || ch.name === "UpdateOp") {
      sawAssign = true;
      continue;
    }
    if (sawAssign) continue;
    if (ch.name === "VariableName") {
      into.add(textAt(doc, ch));
    } else {
      ch.cursor().iterate((inner) => {
        if (inner.name === "VariableName") into.add(textAt(doc, inner));
        return true;
      });
    }
  }
}

function collectPythonDefinedNames(tree: Tree, doc: EditorView["state"]["doc"]): Set<string> {
  const defined = new Set<string>(PYTHON_BUILTINS);
  for (const mod of PYTHON_STDLIB_MODULES) defined.add(mod);
  tree.iterate({
    enter(node) {
      const name = node.name;
      if (
        name === "FunctionDefinition" ||
        name === "ClassDefinition" ||
        name === "ParamList"
      ) {
        for (let ch = node.node.firstChild; ch; ch = ch.nextSibling) {
          if (ch.name === "VariableName") defined.add(textAt(doc, ch));
        }
      } else if (
        name === "AssignStatement" ||
        name === "UpdateStatement" ||
        name === "NamedExpression"
      ) {
        collectAssignedNames(node.node, doc, defined);
      } else if (
        name === "ImportStatement" ||
        name === "ImportFromStatement" ||
        name === "ForStatement" ||
        name === "WithStatement" ||
        name === "ExceptClause" ||
        name === "MatchAsPattern" ||
        name === "AsPattern"
      ) {
        node.node.cursor().iterate((inner) => {
          if (inner.name === "VariableName") defined.add(textAt(doc, inner));
          if (inner.name === "Body" || inner.name === "Block") return false;
          return true;
        });
      }
    },
  });
  return defined;
}

function isDefinitionContext(varNode: SyntaxNode): boolean {
  const parent = varNode.parent;
  if (!parent) return false;
  if (
    parent.name === "FunctionDefinition" ||
    parent.name === "ClassDefinition" ||
    parent.name === "ParamList" ||
    parent.name === "ImportStatement" ||
    parent.name === "ImportFromStatement"
  ) {
    return true;
  }

  // Левая часть присваивания, в т.ч. a, b = ...
  for (let p: SyntaxNode | null = parent; p; p = p.parent) {
    if (
      p.name === "AssignStatement" ||
      p.name === "UpdateStatement" ||
      p.name === "NamedExpression"
    ) {
      for (let ch = p.firstChild; ch; ch = ch.nextSibling) {
        if (ch.name === "AssignOp" || ch.name === "UpdateOp") {
          return varNode.to <= ch.from;
        }
      }
      return false;
    }
    if (
      p.name === "CallExpression" ||
      p.name === "ExpressionStatement" ||
      p.name === "Script" ||
      p.name === "Body"
    ) {
      break;
    }
  }

  // for x in ... / with ... as x / except E as x
  for (let p: SyntaxNode | null = parent; p; p = p.parent) {
    if (
      p.name === "ForStatement" ||
      p.name === "WithStatement" ||
      p.name === "ExceptClause" ||
      p.name === "AsPattern" ||
      p.name === "MatchAsPattern"
    ) {
      for (let ch = p.firstChild; ch; ch = ch.nextSibling) {
        if (ch.name === "Body" || ch.name === "Block") {
          return varNode.to <= ch.from;
        }
      }
      return true;
    }
    if (p.name === "Script" || p.name === "CallExpression") break;
  }

  return false;
}

/**
 * Неопределённые имена: вызовы `foo(...)` и простые обращения `foo`,
 * которых нет среди builtins / присваиваний / def / import.
 */
function collectPythonNameDiagnostics(view: EditorView): Diagnostic[] {
  const tree = syntaxTree(view.state);
  const doc = view.state.doc;
  const defined = collectPythonDefinedNames(tree, doc);
  const out: Diagnostic[] = [];
  const seen = new Set<string>();

  tree.iterate({
    enter(node) {
      if (node.name !== "VariableName") return;
      if (isDefinitionContext(node.node)) return;

      const id = textAt(doc, node);
      if (!id || defined.has(id)) return;

      const key = `${node.from}:${node.to}:${id}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        from: node.from,
        to: node.to,
        severity: "error",
        message: `Имя «${id}» не определено. Проверьте написание или создайте переменную/функцию.`,
        source: "python-name",
      });
    },
  });

  return out;
}

function collectJsDefinedNames(tree: Tree, doc: EditorView["state"]["doc"]): Set<string> {
  const defined = new Set<string>(JS_BUILTINS);
  tree.iterate({
    enter(node) {
      if (
        node.name === "VariableDefinition" ||
        node.name === "PropertyDefinition"
      ) {
        defined.add(textAt(doc, node));
      }
    },
  });
  return defined;
}

function collectJsNameDiagnostics(view: EditorView): Diagnostic[] {
  const tree = syntaxTree(view.state);
  const doc = view.state.doc;
  const defined = collectJsDefinedNames(tree, doc);
  const out: Diagnostic[] = [];
  const seen = new Set<string>();

  tree.iterate({
    enter(node) {
      if (node.name !== "VariableName") return;
      const parent = node.node.parent;
      if (!parent) return;
      // Свойства после точки не VariableName в callee path for MemberExpression first part is VariableName
      if (parent.name === "VariableDefinition") return;

      const id = textAt(doc, node);
      if (!id || defined.has(id) || JS_BUILTINS.has(id)) return;
      // Пропускаем имена из import (часто VariableName в ImportDeclaration)
      if (parent.name === "ImportDeclaration" || parent.name === "ImportGroup") return;

      const key = `${node.from}:${node.to}:${id}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        from: node.from,
        to: node.to,
        severity: "error",
        message: `Имя «${id}» не определено. Проверьте написание.`,
        source: "js-name",
      });
    },
  });
  return out;
}

function collectDiagnostics(view: EditorView, language: CodeEditorLanguage): Diagnostic[] {
  const syntax = collectSyntaxDiagnostics(view, language);
  if (language === "python") {
    return [...syntax, ...collectPythonNameDiagnostics(view)];
  }
  if (language === "javascript") {
    return [...syntax, ...collectJsNameDiagnostics(view)];
  }
  return syntax;
}

/** Подсветка синтаксических ошибок и неопределённых имён в редакторе кода. */
export function codeEditorLintExtensions(language: CodeEditorLanguage) {
  return [
    linter((view) => collectDiagnostics(view, language), { delay: 280 }),
    lintGutter(),
  ];
}
